import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

import {
  fetchAddCompany,
  fetchEditCompany,
  fetchGetCompanies,
} from 'api/companyApi';
import showSnackbarError from 'utils/showSnackbarError';

const EMPTY_COMPANY = {
  id: null,
  name: '',
  owner: '',
  email: '',
  phone: '',
};

const initialState = {
  isLoaded: false,
  isLoading: false,
};

const companiesAdapter = createEntityAdapter({
  selectId: (company) => company.id,
});

export const getCompanies = createAsyncThunk(
  'companies/getCompanies',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchGetCompanies();

      return response;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const addCompany = createAsyncThunk(
  'companies/addCompany',
  async ({ data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchAddCompany(data);

      return response;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const editCompany = createAsyncThunk(
  'companies/editCompany',
  async ({ data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchEditCompany(data);

      return response;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const companiesSlice = createSlice({
  name: 'companies',
  initialState: companiesAdapter.getInitialState(initialState),
  extraReducers: (builder) => {
    builder
      .addCase(getCompanies.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        companiesAdapter.setMany(state, action.payload);
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(getCompanies.rejected, (state) => {
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(addCompany.fulfilled, (state, action) => {
        companiesAdapter.setOne(state, action.payload);
      })
      .addCase(editCompany.fulfilled, (state, action) => {
        companiesAdapter.setOne(state, action.payload);
      });
  },
});

const { selectAll, selectById } = companiesAdapter.getSelectors(
  (state) => state.companies
);

const all = createSelector(selectAll, (state) => state);

const byId = (id) =>
  createSelector(
    (state) => state,
    (state) => selectById(state, id) || EMPTY_COMPANY
  );
const firstCompany = createSelector(
  selectAll,
  (state) => state[0] || EMPTY_COMPANY
);

const selectSelf = (state) => state.companies;
const isLoading = createSelector(selectSelf, (state) => state.isLoading);
const isLoaded = createSelector(selectSelf, (state) => state.isLoaded);

export const CompaniesSelectors = {
  all,
  firstCompany,
  byId,
  isLoading,
  isLoaded,
};

export default companiesSlice.reducer;
